import { lazy } from "react";
import { P } from "const/permissions";

// Components
import Loadable from "components/Loadable";
import ProtectedRoute from "components/ProtectedRoute";

// Auth & Common Views
import ChangePassword from "views/auth/ChangePassword";
import NotFound from "views/NotFound";

// Accounts
import Accounts from "views/admin/Accounts";
import AddAccounts from "views/admin/Accounts/AddAccounts";
import AddProvider from "views/admin/Accounts/AddProvider";

// Check In
import CheckInData from "views/admin/CheckInForm/CheckInData";
import ViewCheckIn from "views/admin/CheckInForm/ViewCheckIn";
import PrEnquiriesTable from "views/admin/Clients/PrEnquiries/PrEnquiriesTable";
import PrEnquiryViewPage from "views/admin/Clients/PrEnquiries/PrEnquiryViewPage";

// Check List
import CheckList from "views/admin/CheckList";

// Clients
import AddClient from "views/admin/Clients/AddClient";
import ArchivedClients from "views/admin/Clients/ArchivedClient";
import Clients from "views/admin/Clients";
import ViewClient from "views/admin/Clients/ViewClient";
import Documents from "views/admin/Documents";

// Course
import AddCourse from "views/admin/Course/AddCourse";
import Course from "views/admin/Course";

// Invoice
import AddInvoice from "views/admin/Invoice/AddInvoice";
import Invoice from "views/admin/Invoice";
import SalaryPayslip from "views/admin/Invoice/SalaryPayslip";
import ViewInvoice from "views/admin/Invoice/ViewInvoice";

// Insurance
import AddInsurance from "views/admin/Insurance/AddInsurance";
import Insurance from "views/admin/Insurance";

// Notifications
import Notifications from "views/admin/Notifications";
import Marketing from "views/admin/Marketing/Marketing";

// Organisation (Consultancy)
import AddOrganisation from "views/admin/Organisation/AddOrganisation";
import Organisation from "views/admin/Organisation";
import ViewOrganisation from "views/admin/Organisation/ViewOrganisation";

// Price List
import PriceList from "views/admin/PriceList";

// Profit Loss
import ProfitLoss from "views/admin/ProfitLoss";

// Document Links
import DocumentLinks from "views/admin/DocumentLinks";

// Referral
import Referral from "views/admin/Referral/index";
import PartnerDetail from "views/admin/Referral/PartnerDetail/index";
import ReferredClients from "views/admin/Referral/ReferredClients/index";
import ReferralClients from "views/admin/Referral/ReferralClients";
import ReferralEntries from "views/admin/Referral/ReferralEntries";
import ReferralProgramContentPage from "views/admin/Referral/ReferralProgramContentPage";
import SupportTicketsPage from "views/admin/Referral/Support/SupportTicketsPage";
import ReferrerReview from "views/admin/Referral/ReferrerReview";
import ReferralApplicationQueue from "views/admin/Referral/ReferralApplicationQueue";
import SupportTicketDetailPage from "views/admin/Referral/Support/SupportTicketDetailPage";

// Reminders
import Reminders from "views/admin/Reminders";
import Emails from "views/admin/Emails";
import ComposeEmailPage from "views/admin/Emails/ComposeEmailPage";

// Appointments
import Appointments from "views/admin/Appointments";

// Intake Forms
import IntakeForms from "views/admin/IntakeForms";

// RPL Certificate
import AddRPLClient from "views/admin/RPLClient/AddRPL";
import RPLClient from "views/admin/RPLClient";

// RPL Course
import AddRPLCourse from "views/admin/RPLCourses/AddRPLCourse";
import RPLCourse from "views/admin/RPLCourses/RPLCourse";
import ViewRPLCourse from "views/admin/RPLCourses/ViewRPLCourse";

// Settings
import OtherSettings from "views/admin/Settings/OtherSettings";

// Skill Assessment
import AddSkillAssessment from "views/admin/SkillAssessment/AddSkillAssessment";
import SkillAssessment from "views/admin/SkillAssessment";
import ClientModuleEntriesPage from "views/admin/shared/moduleClients/ClientModuleEntriesPage";

// Students (Admission)
import AddStudent from "views/admin/Admissions/AddAdmissions";
import StudentDetails from "views/admin/Admissions/AdmissionDetails";
import AdmissionFeeDetailsPage from "views/admin/Admissions/AdmissionFeeDetailsPage";
import Students from "views/admin/Admissions";
import ClientAdmissionsPage from "views/admin/Admissions/ClientAdmissionsPage";

// Tasks
import AddTasks from "views/admin/Tasks/AddTasks";
import Tasks from "views/admin/Tasks";
import ViewTask from "views/admin/Tasks/ViewTask";

// Timesheets
import AddEmployee from "views/admin/Timesheet/AddEmployee";
import EmployeeTimesheet from "views/admin/Timesheet/EmployeeTimesheet";
import Timesheets from "views/admin/Timesheet";

// University
import AddUni from "views/admin/University/AddUni";
import AdmissionSummaryReport from "views/admin/University/AdmissionSummary";
import AddUniCourse from "views/admin/University/AddUniCourse";
import CollegeDocumentSends from "views/admin/CollegeDocumentSends";
import DocumentLibrary from "views/admin/University/DocumentLibrary";
import MappingPage from "views/admin/University/DocumentLibrary/MappingPage";
import University from "views/admin/University";

// Users
import AddUsers from "views/admin/Users/AddUsers";
import Users from "views/admin/Users";
import ViewUserDetail from "views/admin/Users/ViewUserDetail";

// What's New
import WhatsNew from "views/admin/WhatsNew";

// Visa
import AddVisaDetails from "views/admin/Visa/AddVisa";
import Visa from "views/admin/Visa";
import FollowupDetailView from "views/admin/Followup/FollowupDetailView";
import FollowUp from "views/admin/Followup";
import DuplicateClients from "views/admin/Clients/DuplicateClients";

// render - dashboard
const Admin = Loadable(lazy(() => import("layouts/Admin")));
const Dashboard = Loadable(lazy(() => import("views/admin/Dashboard")));
const Settings = Loadable(lazy(() => import("views/admin/Settings/Settings")));
const Profile = Loadable(lazy(() => import("views/admin/Profile")));
const AppPreferences = Loadable(lazy(() => import("views/admin/Preferences/AppPreferences")));
const AdminRoutes = {
  path: "/admin",
  element: <Admin />,
  children: [
    { path: "*", element: <NotFound /> },
    {
      path: "",
      element: (
        <ProtectedRoute>
          <Dashboard />
        </ProtectedRoute>
      ),
    },

    {
      path: "dashboard",
      element: (
        <ProtectedRoute>
          <Dashboard />
        </ProtectedRoute>
      ),
    },
    {
      path: "settings",
      element: (
        <ProtectedRoute requiredPermission={P.SYSTEM.SETTINGS_READ}>
          <Settings />
        </ProtectedRoute>
      ),
    },

    {
      path: "notifications",
      element: (
        <ProtectedRoute requiredPermission={P.NOTIFICATIONS.READ}>
          <Notifications />
        </ProtectedRoute>
      ),
    },
    {
      path: "marketing",
      element: (
        <ProtectedRoute
          requiredPermission={[
            P.MARKETING.ADS_READ,
            P.MARKETING.ADS_CREATE,
            P.MARKETING.ADS_MANAGE,
            P.MARKETING.ACCOUNT_CONNECT,
          ]}
        >
          <Marketing />
        </ProtectedRoute>
      ),
    },
    {
      path: "emails",
      element: (
        <ProtectedRoute requiredPermission={[P.EMAIL.MESSAGE_READ, P.MESSAGING.WHATSAPP_READ]}>
          <Emails />
        </ProtectedRoute>
      ),
    },
    {
      path: "emails/compose",
      element: (
        <ProtectedRoute requiredPermission={[P.EMAIL.MESSAGE_COMPOSE, P.MESSAGING.WHATSAPP_SEND]}>
          <ComposeEmailPage />
        </ProtectedRoute>
      ),
    },
    {
      path: "document-links",
      element: (
        <ProtectedRoute requiredPermission={P.ADMISSIONS.READ}>
          <DocumentLinks />
        </ProtectedRoute>
      ),
    },
    {
      path: "reminders",
      element: (
        <ProtectedRoute requiredPermission={P.REMINDERS.READ}>
          <Reminders />
        </ProtectedRoute>
      ),
    },
    {
      path: "appointments",
      element: (
        <ProtectedRoute requiredPermission={P.APPOINTMENTS.READ}>
          <Appointments />
        </ProtectedRoute>
      ),
    },
    {
      path: "intake-forms",
      element: (
        <ProtectedRoute requiredPermission={P.FORMS.INTAKE_READ}>
          <IntakeForms />
        </ProtectedRoute>
      ),
    },

    {
      path: "client",
      element: (
        <ProtectedRoute requiredPermission={P.CLIENTS.READ}>
          <Clients />
        </ProtectedRoute>
      ),
    },
    {
      path: "client/archived",
      element: (
        <ProtectedRoute requiredPermission={P.CLIENTS.READ}>
          <ArchivedClients />
        </ProtectedRoute>
      ),
    },
    {
      path: "client/duplicate",
      element: (
        <ProtectedRoute requiredPermission={P.CLIENTS.READ}>
          <DuplicateClients />
        </ProtectedRoute>
      ),
    },
    {
      path: "client/check-in",
      element: (
        <ProtectedRoute requiredPermission={P.CLIENTS.READ}>
          <CheckInData />
        </ProtectedRoute>
      ),
    },
    {
      path: "client/pr-enquiries",
      element: (
        <ProtectedRoute requiredPermission={P.PR_ENQUIRY.READ}>
          <PrEnquiriesTable />
        </ProtectedRoute>
      ),
    },
    {
      path: "client/pr-enquiries/:id",
      element: (
        <ProtectedRoute requiredPermission={P.PR_ENQUIRY.READ}>
          <PrEnquiryViewPage />
        </ProtectedRoute>
      ),
    },
    {
      path: "client/check-in/view/:id",
      element: (
        <ProtectedRoute requiredPermission={P.CLIENTS.READ}>
          <ViewCheckIn />
        </ProtectedRoute>
      ),
    },
    {
      path: "client/add",
      element: (
        <ProtectedRoute requiredPermission={P.CLIENTS.READ}>
          <AddClient />
        </ProtectedRoute>
      ),
    },
    {
      path: "client/add",
      element: (
        <ProtectedRoute requiredPermission={P.CLIENTS.READ}>
          <AddClient />
        </ProtectedRoute>
      ),
    },
    {
      path: "client/edit/:id",
      element: (
        <ProtectedRoute requiredPermission={P.CLIENTS.READ}>
          <AddClient />
        </ProtectedRoute>
      ),
    },
    {
      path: "client/view/:id",
      element: (
        <ProtectedRoute requiredPermission={P.CLIENTS.READ}>
          <ViewClient />
        </ProtectedRoute>
      ),
    },
    {
      path: "documents",
      element: (
        <ProtectedRoute requiredPermission="client">
          <Documents />
        </ProtectedRoute>
      ),
    },

    {
      path: "account",
      element: (
        <ProtectedRoute requiredPermission={P.ACCOUNTS.READ}>
          <Accounts />
        </ProtectedRoute>
      ),
    },
    {
      path: "profit-loss",
      element: (
        <ProtectedRoute requiredPermission={P.PROFIT_LOSS.READ}>
          <ProfitLoss />
        </ProtectedRoute>
      ),
    },
    {
      path: "account/add",
      element: (
        <ProtectedRoute requiredPermission={P.ACCOUNTS.READ}>
          <AddAccounts />
        </ProtectedRoute>
      ),
    },
    {
      path: "account/provider/add",
      element: (
        <ProtectedRoute requiredPermission={P.ACCOUNTS.READ}>
          <AddProvider />
        </ProtectedRoute>
      ),
    },
    {
      path: "price-list",
      element: (
        <ProtectedRoute requiredPermission={P.PRICELIST.READ}>
          <PriceList />
        </ProtectedRoute>
      ),
    },
    {
      path: "check-list",
      element: (
        <ProtectedRoute>
          <CheckList />
        </ProtectedRoute>
      ),
    },

    {
      path: "admin-referral",
      element: (
        <ProtectedRoute requiredPermission={P.REFERRAL.READ}>
          <Referral />
        </ProtectedRoute>
      ),
    },
    {
      path: "admin-referral/partners/:id",
      element: (
        <ProtectedRoute requiredPermission={P.REFERRAL.PARTNER_READ}>
          <PartnerDetail />
        </ProtectedRoute>
      ),
    },
    {
      path: "referral/referred-clients",
      element: (
        <ProtectedRoute requiredPermission={P.REFERRAL.LEAD_READ}>
          <ReferredClients />
        </ProtectedRoute>
      ),
    },
    {
      path: "admin-referral/program",
      element: (
        <ProtectedRoute requiredPermission={P.REFERRAL.READ}>
          <ReferralProgramContentPage />
        </ProtectedRoute>
      ),
    },
    {
      path: "admin-referral/referrer-review",
      element: (
        <ProtectedRoute requiredPermission={P.REFERRAL.ATTRIBUTION_ASSIGN}>
          <ReferrerReview />
        </ProtectedRoute>
      ),
    },
    {
      path: "admin-referral/application-requests",
      element: (
        <ProtectedRoute requiredPermission={P.REFERRAL.APPLICATION_READ}>
          <ReferralApplicationQueue />
        </ProtectedRoute>
      ),
    },
    {
      path: "admin-referral/support",
      element: (
        <ProtectedRoute requiredPermission={P.REFERRAL.MESSAGE_READ}>
          <SupportTicketsPage />
        </ProtectedRoute>
      ),
    },
    {
      path: "admin-referral/support/:ticketId",
      element: (
        <ProtectedRoute requiredPermission={P.REFERRAL.MESSAGE_READ}>
          <SupportTicketDetailPage />
        </ProtectedRoute>
      ),
    },
    {
      path: "referral/clients",
      element: (
        <ProtectedRoute requiredPermission={P.REFERRAL.READ}>
          <ReferralClients />
        </ProtectedRoute>
      ),
    },
    {
      path: "admin-referral/:id/clients",
      element: (
        <ProtectedRoute requiredPermission={P.REFERRAL.READ}>
          <ReferralClients />
        </ProtectedRoute>
      ),
    },
    {
      path: "admin-referral/:id/clients/:clientId",
      element: (
        <ProtectedRoute requiredPermission={P.REFERRAL.READ}>
          <ReferralEntries />
        </ProtectedRoute>
      ),
    },

    {
      path: "tasks",
      element: (
        <ProtectedRoute requiredPermission={P.TASKS.READ}>
          <Tasks />
        </ProtectedRoute>
      ),
    },
    {
      path: "tasks/add",
      element: (
        <ProtectedRoute requiredPermission={P.TASKS.READ}>
          <AddTasks />
        </ProtectedRoute>
      ),
    },
    {
      path: "tassk/edit/:id",
      element: (
        <ProtectedRoute requiredPermission={P.TASKS.READ}>
          <AddTasks />
        </ProtectedRoute>
      ),
    },
    {
      path: "tasks/view/:id",
      element: (
        <ProtectedRoute requiredPermission={P.TASKS.READ}>
          <ViewTask />
        </ProtectedRoute>
      ),
    },
    {
      path: "timesheets",
      element: (
        <ProtectedRoute requiredPermission={P.TIMESHEETS.READ}>
          <Timesheets />
        </ProtectedRoute>
      ),
    },
    {
      path: "timesheets/view/:id",
      element: (
        <ProtectedRoute requiredPermission={P.TIMESHEETS.READ}>
          <EmployeeTimesheet />
        </ProtectedRoute>
      ),
    },
    {
      path: "timesheets/add",
      element: (
        <ProtectedRoute requiredPermission={P.TIMESHEETS.READ}>
          <AddEmployee />
        </ProtectedRoute>
      ),
    },
    {
      path: "timesheets/edit/:id",
      element: (
        <ProtectedRoute requiredPermission={P.TIMESHEETS.READ}>
          <AddEmployee />
        </ProtectedRoute>
      ),
    },
    {
      path: "university",
      element: (
        <ProtectedRoute requiredPermission={P.UNIVERSITY.READ}>
          <University />
        </ProtectedRoute>
      ),
    },
    {
      path: "university/report",
      element: (
        <ProtectedRoute requiredPermission={P.UNIVERSITY.READ}>
          <ProtectedRoute requiredPermission={P.ADMISSIONS.READ}>
            <AdmissionSummaryReport />
          </ProtectedRoute>
        </ProtectedRoute>
      ),
    },
    {
      path: "university/add",
      element: (
        <ProtectedRoute requiredPermission={P.UNIVERSITY.READ}>
          <AddUni />
        </ProtectedRoute>
      ),
    },
    {
      path: "university/edit/:id",
      element: (
        <ProtectedRoute requiredPermission={P.UNIVERSITY.READ}>
          <AddUni />
        </ProtectedRoute>
      ),
    },
    {
      path: "university/:universityId/documents",
      element: (
        <ProtectedRoute requiredPermission={P.UNIVERSITY_DOCUMENT.READ}>
          <DocumentLibrary />
        </ProtectedRoute>
      ),
    },
    {
      path: "university/:universityId/documents/sent",
      element: (
        <ProtectedRoute requiredPermission={P.UNIVERSITY_DOCUMENT.READ}>
          <DocumentLibrary />
        </ProtectedRoute>
      ),
    },
    {
      path: "university/:universityId/documents/:fileId/mapping",
      element: (
        <ProtectedRoute requiredPermission={P.UNIVERSITY_DOCUMENT.READ}>
          <MappingPage />
        </ProtectedRoute>
      ),
    },
    {
      path: "college-document-sends",
      element: (
        <ProtectedRoute requiredPermission={P.UNIVERSITY_DOCUMENT.READ}>
          <CollegeDocumentSends />
        </ProtectedRoute>
      ),
    },
    {
      path: "university/:uniId/course/add",
      element: (
        <ProtectedRoute requiredPermission={P.UNIVERSITY.READ}>
          <AddUniCourse />
        </ProtectedRoute>
      ),
    },
    {
      path: "university/:uniId/course/edit/:courseId",
      element: (
        <ProtectedRoute requiredPermission={P.UNIVERSITY.READ}>
          <AddUniCourse />
        </ProtectedRoute>
      ),
    },

    {
      path: "admission",
      element: (
        <ProtectedRoute requiredPermission={P.ADMISSIONS.READ}>
          <Students />
        </ProtectedRoute>
      ),
    },
    {
      path: "admission/client/:clientId",
      element: (
        <ProtectedRoute requiredPermission={P.ADMISSIONS.READ}>
          <ClientAdmissionsPage />
        </ProtectedRoute>
      ),
    },
    {
      path: "admission/add",
      element: (
        <ProtectedRoute requiredPermission={P.ADMISSIONS.READ}>
          <AddStudent />
        </ProtectedRoute>
      ),
    },
    {
      path: "admission/edit/:id",
      element: (
        <ProtectedRoute requiredPermission={P.ADMISSIONS.READ}>
          <AddStudent />
        </ProtectedRoute>
      ),
    },
    {
      path: "admission/fees/:id",
      element: (
        <ProtectedRoute requiredPermission={P.ADMISSIONS.READ}>
          <AdmissionFeeDetailsPage />
        </ProtectedRoute>
      ),
    },

    {
      path: "admission/view",
      element: (
        <ProtectedRoute requiredPermission={P.ADMISSIONS.READ}>
          <StudentDetails />
        </ProtectedRoute>
      ),
    },

    {
      path: "consultancy",
      element: (
        <ProtectedRoute requiredPermission={P.CONSULTANCY.READ}>
          <Organisation />
        </ProtectedRoute>
      ),
    },

    {
      path: "consultancy/add",
      element: (
        <ProtectedRoute requiredPermission={P.CONSULTANCY.CREATE}>
          <AddOrganisation />
        </ProtectedRoute>
      ),
    },
    {
      path: "consultancy/edit/:id",
      element: (
        <ProtectedRoute requiredPermission={P.CONSULTANCY.UPDATE}>
          <AddOrganisation />
        </ProtectedRoute>
      ),
    },

    {
      path: "consultancy/view/:id",
      element: (
        <ProtectedRoute requiredPermission={P.CONSULTANCY.READ}>
          <ViewOrganisation />
        </ProtectedRoute>
      ),
    },
    {
      path: "insurance",
      element: (
        <ProtectedRoute requiredPermission={P.INSURANCE.READ}>
          <Insurance />
        </ProtectedRoute>
      ),
    },
    {
      path: "insurance/client/:clientId",
      element: (
        <ProtectedRoute requiredPermission={P.INSURANCE.READ}>
          <ClientModuleEntriesPage moduleKey="insurance" />
        </ProtectedRoute>
      ),
    },
    {
      path: "insurance/add",
      element: (
        <ProtectedRoute requiredPermission={P.INSURANCE.READ}>
          <AddInsurance />
        </ProtectedRoute>
      ),
    },
    {
      path: "insurance/edit/:id",
      element: (
        <ProtectedRoute requiredPermission={P.INSURANCE.READ}>
          <AddInsurance />
        </ProtectedRoute>
      ),
    },
    {
      path: "insurance/client/:clientId",
      element: (
        <ProtectedRoute requiredPermission={P.INSURANCE.READ}>
          <ClientModuleEntriesPage moduleKey="insurance" />
        </ProtectedRoute>
      ),
    },
    {
      path: "visa",
      element: (
        <ProtectedRoute requiredPermission={P.VISA.READ}>
          <Visa />
        </ProtectedRoute>
      ),
    },
    {
      path: "visa/add",
      element: (
        <ProtectedRoute requiredPermission={P.VISA.READ}>
          <AddVisaDetails />
        </ProtectedRoute>
      ),
    },
    {
      path: "visa/edit/:id",
      element: (
        <ProtectedRoute requiredPermission={P.VISA.READ}>
          <AddVisaDetails />
        </ProtectedRoute>
      ),
    },
    {
      path: "visa/client/:clientId",
      element: (
        <ProtectedRoute requiredPermission={P.VISA.READ}>
          <ClientModuleEntriesPage moduleKey="visa" />
        </ProtectedRoute>
      ),
    },
    {
      path: "skill-assessment",
      element: (
        <ProtectedRoute requiredPermission={P.SKILL.READ}>
          <SkillAssessment />
        </ProtectedRoute>
      ),
    },
    {
      path: "skill-assessment/client/:clientId",
      element: (
        <ProtectedRoute requiredPermission={P.SKILL.READ}>
          <ClientModuleEntriesPage moduleKey="skill" />
        </ProtectedRoute>
      ),
    },
    {
      path: "skill-assessment/add",
      element: (
        <ProtectedRoute requiredPermission={P.SKILL.READ}>
          <AddSkillAssessment />
        </ProtectedRoute>
      ),
    },
    {
      path: "skill-assessment/edit/:id",
      element: (
        <ProtectedRoute requiredPermission={P.SKILL.READ}>
          <AddSkillAssessment />
        </ProtectedRoute>
      ),
    },
    {
      path: "rpl-client",
      element: (
        <ProtectedRoute requiredPermission={P.RPL_CLIENT.READ}>
          <RPLClient />
        </ProtectedRoute>
      ),
    },
    {
      path: "rpl-client/client/:clientId",
      element: (
        <ProtectedRoute requiredPermission={P.RPL_CLIENT.READ}>
          <ClientModuleEntriesPage moduleKey="rpl" />
        </ProtectedRoute>
      ),
    },
    {
      path: "rpl-client/add",
      element: (
        <ProtectedRoute requiredPermission={P.RPL_CLIENT.READ}>
          <AddRPLClient />
        </ProtectedRoute>
      ),
    },
    {
      path: "rpl-client/edit/:id",
      element: (
        <ProtectedRoute requiredPermission={P.RPL_CLIENT.READ}>
          <AddRPLClient />
        </ProtectedRoute>
      ),
    },
    {
      path: "rpl-course",
      element: (
        <ProtectedRoute requiredPermission={P.RPL_COURSE.READ}>
          <RPLCourse />
        </ProtectedRoute>
      ),
    },
    {
      path: "rpl-course/add",
      element: (
        <ProtectedRoute requiredPermission={P.RPL_COURSE.READ}>
          <AddRPLCourse />
        </ProtectedRoute>
      ),
    },
    {
      path: "rpl-course/view/:id",
      element: (
        <ProtectedRoute requiredPermission={P.RPL_COURSE.READ}>
          <ViewRPLCourse />
        </ProtectedRoute>
      ),
    },
    {
      path: "rpl-client/edit/:id",
      element: (
        <ProtectedRoute requiredPermission={P.RPL_CLIENT.READ}>
          <AddRPLClient />
        </ProtectedRoute>
      ),
    },
    {
      path: "course",
      element: (
        <ProtectedRoute requiredPermission={P.COURSE.READ}>
          <Course />
        </ProtectedRoute>
      ),
    },
    {
      path: "course/add",
      element: (
        <ProtectedRoute requiredPermission={P.COURSE.READ}>
          <AddCourse />
        </ProtectedRoute>
      ),
    },
    {
      path: "course/edit/:id",
      element: (
        <ProtectedRoute requiredPermission={P.COURSE.READ}>
          <AddCourse />
        </ProtectedRoute>
      ),
    },
    {
      path: "user",
      element: (
        <ProtectedRoute requiredPermission={P.USERS.READ}>
          <Users />
        </ProtectedRoute>
      ),
    },
    {
      path: "user/view/:id",
      element: (
        <ProtectedRoute requiredPermission={P.USERS.READ}>
          <ViewUserDetail />
        </ProtectedRoute>
      ),
    },
    {
      path: "invoice",
      element: (
        <ProtectedRoute requiredPermission={P.INVOICE.READ}>
          <Invoice />
        </ProtectedRoute>
      ),
    },
    {
      path: "invoice/add",
      element: (
        <ProtectedRoute requiredPermission={[P.INVOICE.READ, P.INVOICE.CLIENT_CREATE]}>
          <AddInvoice />
        </ProtectedRoute>
      ),
    },
    {
      path: "invoice/salary-payslip",
      element: (
        <ProtectedRoute requiredPermission={P.INVOICE.READ}>
          <SalaryPayslip />
        </ProtectedRoute>
      ),
    },
    {
      path: "invoice/edit/:id",
      element: (
        <ProtectedRoute requiredPermission={P.INVOICE.READ}>
          <AddInvoice />
        </ProtectedRoute>
      ),
    },
    {
      path: "invoice/view/:id",
      element: (
        <ProtectedRoute requiredPermission={P.INVOICE.READ}>
          <ViewInvoice />
        </ProtectedRoute>
      ),
    },
    {
      path: "user/add",
      element: (
        <ProtectedRoute requiredPermission={P.USERS.READ}>
          <AddUsers />
        </ProtectedRoute>
      ),
    },
    {
      path: "user/edit/:id",
      element: (
        <ProtectedRoute requiredPermission={P.USERS.READ}>
          <AddUsers />
        </ProtectedRoute>
      ),
    },
    {
      path: "profile",
      element: (
        <ProtectedRoute requiredPermission="">
          <Profile />
        </ProtectedRoute>
      ),
    },
    {
      path: "preferences",
      element: (
        <ProtectedRoute>
          <AppPreferences />
        </ProtectedRoute>
      ),
    },
    {
      path: "whats-new",
      element: (
        <ProtectedRoute>
          <WhatsNew />
        </ProtectedRoute>
      ),
    },

    {
      path: "setting",
      element: (
        <ProtectedRoute requiredPermission={P.SYSTEM.SETTINGS_READ}>
          <Settings />
        </ProtectedRoute>
      ),
    },
    {
      path: "settings/other",
      element: (
        <ProtectedRoute requiredPermission={P.SYSTEM.SETTINGS_READ}>
          <OtherSettings />
        </ProtectedRoute>
      ),
    },

    {
      path: "change/password/",
      element: (
        <ProtectedRoute>
          <ChangePassword />
        </ProtectedRoute>
      ),
    },
    {
      path: "follow-up",
      element: (
        <ProtectedRoute requiredPermission={P.FOLLOWUP.READ}>
          <FollowUp />
        </ProtectedRoute>
      ),
    },
    {
      path: "follow-up/view/:clientId/:followUpId",
      element: (
        <ProtectedRoute requiredPermission={P.FOLLOWUP.READ}>
          <FollowupDetailView />
        </ProtectedRoute>
      ),
    },
  ],
};

export default AdminRoutes;
